import { useEffect, useRef } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { notificationsService, type NotificationList, type NotificationListParams } from '@/shared/services/notifications.service';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { useAuthStore } from '@/shared/stores/auth.store';
import { api } from '@/shared/services/api';
import { useToast } from '@/shared/components/Toast';
import { useTranslation } from 'react-i18next';
import { useRealtimeSubscription } from './useRealtimeSubscription';

export const NOTIFICATIONS_KEY = 'notifications';

/** A page of the history. */
export const NOTIFICATION_HISTORY_PAGE = 30;

/**
 * A safety net only: new notifications arrive live through Supabase Realtime
 * (postgres_changes on `notifications`, in the supabase_realtime publication
 * since migration 20261103000410). The poll covers a dropped socket.
 */
const FALLBACK_POLL_MS = 5 * 60_000;

/**
 * The API caches GET /notifications for 30 s per user and the worker drops
 * that cache right after writing the row; a refetch that raced the drop
 * would read the old list, so a change is fetched again a moment later.
 */
const SETTLE_REFETCH_MS = 1500;

function refetchNotifications(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
}

/**
 * Keeps every notifications query live: one Realtime subscription to the
 * reader's own rows (RLS keeps them to user_id = auth.uid()), refetching on
 * insert (a new item) and update (read in another tab).
 */
export function useNotificationsLive(): void {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  useRealtimeSubscription({
    table: 'notifications',
    filter: userId ? `user_id=eq.${userId}` : undefined,
    events: ['INSERT', 'UPDATE', 'DELETE'],
    invalidateKeys: [[NOTIFICATIONS_KEY]],
    onEvent: () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => refetchNotifications(queryClient), SETTLE_REFETCH_MS);
    },
    enabled: !!userId,
  });
}

/** The bell's list: unread, newest first, from every brand the reader is in. */
export function useNotifications(params?: NotificationListParams) {
  useNotificationsLive();
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery<NotificationList>({
    queryKey: [NOTIFICATIONS_KEY, params, wsId],
    queryFn: () => notificationsService.list(params),
    refetchInterval: FALLBACK_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 60_000,
    enabled: !!wsId,
  });
}

/** The history page (M10): pages of NOTIFICATION_HISTORY_PAGE, filtered. */
export function useNotificationHistory(filter: Omit<NotificationListParams, 'page' | 'limit'>) {
  useNotificationsLive();
  return useInfiniteQuery<NotificationList>({
    queryKey: [NOTIFICATIONS_KEY, 'history', filter],
    queryFn: ({ pageParam }) => notificationsService.list({ ...filter, page: pageParam as number, limit: NOTIFICATION_HISTORY_PAGE }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.data.length, 0);
      return typeof last.total === 'number' && loaded < last.total && last.data.length > 0 ? pages.length + 1 : undefined;
    },
    refetchInterval: FALLBACK_POLL_MS,
    staleTime: 60_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => notificationsService.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
    },
    onError: () => toast('error', t('notifications.markReadFailed')),
  });
}

/** Marks a set read at once (the history's bulk action). */
export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (ids: string[]) => notificationsService.markManyRead(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
    },
    onError: () => toast('error', t('notifications.markAllReadFailed')),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (workspaceId?: string) => notificationsService.markAllRead(workspaceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
    },
    onError: () => toast('error', t('notifications.markAllReadFailed')),
  });
}

export interface TestNotificationResult {
  ok: boolean;
  queued: boolean;
  /** False when the server has no email provider: only the bell can arrive. */
  emailConfigured: boolean;
  sentAt: string;
}

/** Sends a self-test notification through the real pipeline (the queue and
 *  the worker) so the user can see the bell and, when email is set up, the email. */
export function useSendTestNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<TestNotificationResult> => {
      const res = await api.post<TestNotificationResult>('/notifications/test');
      return res.data;
    },
    onSuccess: () => {
      refetchNotifications(queryClient);
    },
  });
}
